import { AutoOcoBracketModel, AutoOcoBracketValueType, OrderType, SymbolMetadata, UserContractModel } from '@/api/userApi';
import { OrderPromptType } from '@/contexts/OrdersContext';
import Decimal from 'decimal.js';
import { number } from 'yup';

export const roundToTickSize = (baseNumber: number, tickSize: number) => {
  return new Decimal(baseNumber).div(tickSize).round().mul(tickSize).toNumber();
};
export interface IBracketValues {
  stopLoss?: number;
  takeProfit?: number;
  stopLossType?: OrderType;
  takeProfitType?: OrderType;
}

export const getBracketValues = (amount: number, bracket: AutoOcoBracketModel, tickValue: number, side: OrderPromptType): IBracketValues =>
{
  const retn: IBracketValues = {};
  if (bracket.stopLossValue) {
    let ticks = undefined;
    if(bracket.stopLossValueType == AutoOcoBracketValueType.Tick) {
      ticks = Math.floor(bracket.stopLossValue);
    } else if(bracket.stopLossValueType == AutoOcoBracketValueType.Dollar) {
      var totalContracts = Math.abs(amount);
      //get the dollar profit target per 1 contract
      var valuePerContract = bracket.stopLossValue / totalContracts;
      //calculate the number of ticks in that value (ex: 10$ , with tick 5$ value, 2 ticks)
      var valueInTicks = Math.ceil(valuePerContract / tickValue);
      ticks = valueInTicks;
    } else {
      ticks = undefined;
    }
    retn.stopLoss = ticks;
    retn.stopLossType = bracket.stopLossType;


    if (side === OrderPromptType.Buy) {
      retn.stopLoss = -retn.stopLoss;
    }
  }
  if (bracket.takeProfitValue) {
    let ticks = undefined;
    if(bracket.takeProfitValueType == AutoOcoBracketValueType.Tick) {
      ticks = Math.floor(bracket.takeProfitValue);
    } else if(bracket.takeProfitValueType == AutoOcoBracketValueType.Dollar) {
      var totalContracts = Math.abs(amount);
      //get the dollar profit target per 1 contract
      var valuePerContract = bracket.takeProfitValue / totalContracts;
      //calculate the number of ticks in that value (ex: 10$ , with tick 5$ value, 2 ticks)
      var valueInTicks = Math.ceil(valuePerContract / tickValue);
      ticks = valueInTicks;
    } else {
      ticks = undefined;
    }

    retn.takeProfit = ticks;
    retn.takeProfitType = bracket.takeProfitType;

    if (side !== OrderPromptType.Buy) {
      retn.takeProfit = -retn.takeProfit;
    }
  }
  return retn;
}

export const convertToFractional = (base: number, priceScale: number, minMov: number, minMov2: number | null = null) => {
  const baseDecimal = new Decimal(Math.abs(base));
  const truncated = baseDecimal.trunc();
  const decimals = baseDecimal.sub(truncated);
  const isNegative = base < 0;
  if (minMov2 === null || minMov2 === 0) {
    var firstFraction = decimals.mul(priceScale);
    var firstFractionTruncated = firstFraction.trunc();

    return `${isNegative ? '-' : ''}${truncated.toString()}'${firstFractionTruncated.abs()}`;
  } else {
    var firstFraction = decimals.mul(priceScale).div(minMov2);
    var firstFractionTruncated = firstFraction.trunc();

    var secondFraction = firstFraction.sub(firstFractionTruncated).mul(10);
    var secondFractionTruncated = secondFraction.trunc();

    return `${isNegative ? '-' : ''}${truncated.toString()}'${firstFractionTruncated.abs()}'${secondFractionTruncated.abs()}`;
  }
};

interface FractionNumbers {
  whole: number;
  first: number;
  second: number | null;
}

export const combineFractions = (wholeNumber: number, firstFraction: number, secondFraction: number | null, priceScale: number, minMov: number, minMov2: number | null = null) => {
  if (minMov2 === null || minMov2 === 0) {
    const wholeDecimal = new Decimal(wholeNumber);
    const firstDecimal = new Decimal(firstFraction).div(priceScale);
    return wholeDecimal.add(firstDecimal).toNumber();
  } else {
    const wholeDecimal = new Decimal(wholeNumber);
    const firstDecimal = new Decimal(new Decimal(secondFraction).div(10).add(firstFraction)).mul(minMov2).div(priceScale);
    return wholeDecimal.add(firstDecimal).toNumber();
  }
};

export const getFractions = (base: number, priceScale: number, minMov: number, minMov2: number | null = null): FractionNumbers => {
  const baseDecimal = new Decimal(base);
  const truncated = baseDecimal.trunc();
  const decimals = baseDecimal.sub(truncated);

  if (minMov2 === null || minMov2 === 0) {
    var firstFraction = decimals.mul(priceScale);
    var firstFractionTruncated = firstFraction.trunc();

    return { whole: truncated.toNumber(), first: firstFractionTruncated.toNumber(), second: null };
  } else {
    var firstFraction = decimals.mul(priceScale).div(minMov2);
    var firstFractionTruncated = firstFraction.trunc();

    var secondFraction = firstFraction.sub(firstFractionTruncated).mul(10);
    var secondFractionTruncated = secondFraction.trunc();

    return { whole: truncated.toNumber(), first: firstFractionTruncated.toNumber(), second: secondFractionTruncated.toNumber() };
  }
};

export const roundToNearestTick = (n: number, contract: UserContractModel) => {
  const part1 = new Decimal(n);
  const divided = part1.div(contract.tickSize);
  const rounded = divided.round();
  const multiplied = rounded.mul(contract.tickSize);
  const truncated = multiplied.toDecimalPlaces(contract.decimalPlaces);

  return truncated.toNumber();
};
